import * as MuiIcons from "@mui/icons-material";
import * as Mui from "@mui/material";
import {
  ColDef,
  ICellRendererParams,
  SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useFormik } from "formik";
import { useContext, useEffect, useRef, useState } from "react";
import { customTheme } from "../../../../config/theme/MuiTheme";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import { ContextOperation } from "../../context/OperationContext";
import { useAgGridCustomDatasource } from "../../hooks/useAgGridCustomDatasource";
import EmptyStateGrid from "../common/EmptyStateGrid";
import GenericFilterBox from "../common/GenericFilterBox";
import { nameof } from "../common/ObjectUtils";
import Dialogs from "./dialogs";
import { GroupLocationsType } from "./dialogs/DeleteConfirmation";
import { useTranslation } from "react-i18next";
import { useHasScope } from "../../../../config/globalConfig";

import {
  SupplierCustomGroupLocationsRequests,
  SupplierCustomGroupService,
} from "./service/supplierCustomGroupLocations.actions";
import GroupLocationsTypes from "./Types";
import EnumChangeName from "../common/EnumChangeName";
import AgGridOperationTable from "../common/AgGridOperationTable";

export const SupplierCustomGroupLocation = (props: any) => {
  const hasScope = useHasScope;
  const enumTypeName = EnumChangeName();
  const groupLocationsRef = useRef<AgGridReact>(null);
  const locationRef = useRef<AgGridReact>(null);
  const { tenant } = useContext(ContextOperation);
  const { t } = useTranslation(["operation"]);

  const [groupLocationsId, setGroupLocationsId] = useState<undefined | number>(
    undefined
  );
  const [selectedGroupLocation, setSelectedGroupLocation] = useState<
    undefined | GroupLocationsTypes.GroupLocations
  >(undefined);
  const [selectedLocation, setSelectedLocation] = useState<
    undefined | GroupLocationsTypes.ILocationsValid
  >(undefined);

  const [groupLocationDialog, setGroupLocationDialog] = useState(false);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] =
    useState(false);
  const [deleteType, setDeleteType] =
    useState<GroupLocationsType>("groupLocations");
  const [dialogData, setDialogData] = useState<any>();

  const onCloseGroupLocationsDialog = () => {
    setGroupLocationsId(undefined);
    setGroupLocationDialog(false);
  };

  const onDeleteGroupLocations = (data: any) => {
    setSelectedGroupLocation(data);
    setDeleteType("groupLocations");
    setDeleteConfirmationVisible(true);
  };

  const onDetachLocation = (location: GroupLocationsTypes.ILocationsValid) => {
    setSelectedLocation(location);
    setDeleteType("location");
    setDeleteConfirmationVisible(true);
  };

  const formik = useFormik<SupplierCustomGroupLocationsRequests.GetAll>({
    initialValues: {},
    onSubmit: () => {},
  });

  const [
    groupLocationsDataSource,
    totalGroupLocations,
    updateGroupLocationsDataSource,
  ] = useAgGridCustomDatasource(
    SupplierCustomGroupService.get.all,
    [tenant, formik.values],
    [tenant, formik.submitCount]
  );
  const [locationDataSource, totalLocations, updateLocationDataSource] =
    useAgGridCustomDatasource(
      SupplierCustomGroupService.get.groupLocationsById,
      [tenant, selectedGroupLocation?.id],
      [tenant, selectedGroupLocation?.id]
    );

  const onUpdateDeleteConfirmation = () => {
    if (deleteType === "groupLocations") {
      updateGroupLocationsDataSource();
      setSelectedGroupLocation(undefined);
    } else {
      updateLocationDataSource();
    }
  };

  useEffect(() => {
    setSelectedGroupLocation(undefined);
  }, [tenant]);

  useEffect(() => {
    groupLocationsRef.current?.api.deselectAll();
  }, [formik.submitCount]);

  const groupLocationsColumns: ColDef[] = [
    {
      field: nameof<GroupLocationsTypes.GroupLocations>("name"),
      headerName: t("supplierCustomGroupLocation.table.firstColumnTitle"),
      flex: 1,
    },
    {
      field: nameof<GroupLocationsTypes.GroupLocations>("desc"),
      headerName: t("supplierCustomGroupLocation.table.secondColumnTitle"),
      flex: 1,
      cellRenderer: (
        props: ICellRendererParams<GroupLocationsTypes.GroupLocations>
      ) => {
        return (
          <Mui.Tooltip title={props?.value}>
            <Mui.Box
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
            >
              {props?.value}
            </Mui.Box>
          </Mui.Tooltip>
        );
      },
    },
    {
      field: "",
      maxWidth: 100,
      cellRenderer: (
        props: ICellRendererParams<GroupLocationsTypes.GroupLocations>
      ) => {
        return (
          <Mui.Stack
            direction="row"
            style={{ color: customTheme.palette.neutral.bright }}
          >
            {hasScope("SCOPE_UPDATE_MDSUPPLIER_CUSTOM_GROUP_LOCATION") && (
              <Mui.Tooltip title={t("tooltipTitle.edit")}>
                <Mui.IconButton
                  color="accent"
                  onClick={() => {
                    setGroupLocationDialog(true);
                    setGroupLocationsId(props.data?.id);
                    setDialogData(props.data as any);
                  }}
                >
                  <MuiIcons.Edit />
                </Mui.IconButton>
              </Mui.Tooltip>
            )}

            {hasScope("SCOPE_DELETE_MDSUPPLIER_CUSTOM_GROUP_LOCATION") && (
              <Mui.Tooltip
                title={t("supplierCustomGroupLocation.table.tooltipDelete")}
              >
                <Mui.IconButton
                  color="accent"
                  onClick={() =>
                    props.data?.id && onDeleteGroupLocations(props.data)
                  }
                >
                  <MuiIcons.Delete />
                </Mui.IconButton>
              </Mui.Tooltip>
            )}
          </Mui.Stack>
        );
      },
    },
  ];

  const locationColumns: ColDef[] = [
    {
      field: nameof<GroupLocationsTypes.ILocationsValid>("locationDesc"),
      headerName: t(
        "supplierCustomGroupLocation.locationDetailsTable.firstColumnTitle"
      ),
      flex: 1,
      cellRenderer: (
        props: ICellRendererParams<GroupLocationsTypes.ILocationsValid>
      ) => {
        return (
          <Mui.Tooltip title={props?.value}>
            <Mui.Box
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
            >
              {props?.value}
            </Mui.Box>
          </Mui.Tooltip>
        );
      },
    },
    {
      field: nameof<GroupLocationsTypes.ILocationsValid>("locationType"),
      headerName: t(
        "supplierCustomGroupLocation.locationDetailsTable.secondColumnTitle"
      ),
      flex: 1,
      cellRenderer: (
        props: ICellRendererParams<GroupLocationsTypes.ILocationsValid>
      ) => {
        return enumTypeName.locationType(props.value);
      },
    },
    {
      maxWidth: 40,
      cellStyle: {
        padding: "0px",
        border: "none",
      },
      cellRenderer: (
        props: ICellRendererParams<GroupLocationsTypes.ILocationsValid>
      ) => {
        return (
          <Mui.Stack direction="row">
            {hasScope("SCOPE_UPDATE_MDSUPPLIER_CUSTOM_GROUP_LOCATION") && (
              <Mui.Tooltip
                title={t("supplierCustomGroupLocation.table.tooltipDelete")}
              >
                <Mui.IconButton
                  color="accent"
                  onClick={() =>
                    props.data && onDetachLocation(props.data as any)
                  }
                >
                  <MuiIcons.Delete />
                </Mui.IconButton>
              </Mui.Tooltip>
            )}
          </Mui.Stack>
        );
      },
    },
  ];

  const onSelectionChanged = (
    event: SelectionChangedEvent<GroupLocationsTypes.GroupLocations>
  ) => {
    const selectedRows = event.api.getSelectedRows();
    if (selectedRows && selectedRows.length > 0) {
      setSelectedGroupLocation(selectedRows[0]);
    } else {
      setSelectedGroupLocation(undefined);
    }
  };

  return (
    <>
      <Mui.Box>
        <form id="group-locations-search-form" onSubmit={formik.handleSubmit}>
          <GenericFilterBox
            formId="group-locations-search-form"
            onReset={() => formik.resetForm()}
          >
            <Mui.TextField
              name={nameof<SupplierCustomGroupLocationsRequests.GetAll>("name")}
              label={t("supplierCustomGroupLocation.inputFields.name")}
              value={formik.values.name || ""}
              onChange={formik.handleChange}
              fullWidth={true}
            />

            <Mui.TextField
              name={nameof<SupplierCustomGroupLocationsRequests.GetAll>("desc")}
              label={t("supplierCustomGroupLocation.inputFields.description")}
              value={formik.values.desc || ""}
              onChange={formik.handleChange}
              fullWidth={true}
            />
          </GenericFilterBox>
        </form>

        <Mui.Grid container padding="16px" spacing="16px" height="100%">
          <Mui.Grid item md={8} xs={12}>
            <Mui.Card
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Mui.CardHeader
                action={
                  <>
                    {hasScope(
                      "SCOPE_CREATE_MDSUPPLIER_CUSTOM_GROUP_LOCATION"
                    ) && (
                      <Mui.Tooltip title={t("tooltipTitle.add")}>
                        <Mui.IconButton
                          color="accent"
                          onClick={() => setGroupLocationDialog(true)}
                        >
                          <MuiIcons.Add />
                        </Mui.IconButton>
                      </Mui.Tooltip>
                    )}
                  </>
                }
                title={
                  <Mui.Typography variant="body">
                    {t("supplierCustomGroupLocation.table.tableTitle")}
                  </Mui.Typography>
                }
              />

              <AgGridOperationTable
                gridRef={groupLocationsRef}
                columnDefs={groupLocationsColumns}
                serverSideDatasource={groupLocationsDataSource}
                rowModelType="serverSide"
                serverSideInfiniteScroll={true}
                cacheBlockSize={10}
                rowSelection="single"
                onSelectionChanged={onSelectionChanged}
                totalElements={totalGroupLocations}
                emptyState={{
                  title: t("supplierCustomGroupLocation.table.emptyState.title"),
                  content: t("supplierCustomGroupLocation.table.emptyState.content")
                }}
              />
            </Mui.Card>
          </Mui.Grid>

          <Mui.Grid item md={4} xs={12}>
            <Mui.Card
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <Mui.CardHeader
                action={
                  <>
                    {hasScope(
                      "SCOPE_UPDATE_MDSUPPLIER_CUSTOM_GROUP_LOCATION"
                    ) && (
                      <Mui.Tooltip title={t("tooltipTitle.edit")}>
                        <Mui.IconButton
                          color="accent"
                          onClick={() => {
                            setGroupLocationDialog(true);
                            setGroupLocationsId(selectedGroupLocation?.id);
                            setDialogData(selectedGroupLocation as any);
                          }}
                          disabled={selectedGroupLocation ? false : true}
                        >
                          <MuiIcons.Edit />
                        </Mui.IconButton>
                      </Mui.Tooltip>
                    )}
                  </>
                }
                title={
                  <Mui.Typography variant="body">
                    {t(
                      "supplierCustomGroupLocation.locationDetailsTable.title"
                    )}
                  </Mui.Typography>
                }
              />

              <AgGridOperationTable
                gridRef={locationRef}
                columnDefs={locationColumns}
                cacheBlockSize={10}
                rowModelType="clientSide"
                rowData={selectedGroupLocation?.locations || []}
                totalElements={selectedGroupLocation?.locations.length || 0}
                emptyState={{
                  title: t("supplierCustomGroupLocation.locationDetailsTable.messageTitle"),
                  content: t("supplierCustomGroupLocation.locationDetailsTable.messageDescription")
                }}
              />
            </Mui.Card>
          </Mui.Grid>
        </Mui.Grid>

        <Dialogs.GroupLocationsDialog
          tenant={tenant}
          isOpen={groupLocationDialog}
          onClose={onCloseGroupLocationsDialog}
          groupLocationsId={groupLocationsId}
          data={dialogData}
          onUpdateDatasource={(newData: any) => {
            setSelectedGroupLocation(newData);
            updateGroupLocationsDataSource();
            updateLocationDataSource();
            setDialogData([]);
          }}
        />

        <Dialogs.DeleteConfirmation
          tenant={tenant}
          isOpen={deleteConfirmationVisible}
          onClose={() => setDeleteConfirmationVisible(false)}
          deleteType={deleteType}
          groupLocations={selectedGroupLocation}
          location={selectedLocation}
          onUpdateDatasource={(newData: any) => {
            setSelectedGroupLocation(newData);
            updateGroupLocationsDataSource();
            updateLocationDataSource();
            onUpdateDeleteConfirmation();
          }}
        />
      </Mui.Box>
    </>
  );
};
